import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../utils/api.js";
import { useAuthStore } from "../../store/auth.js";
import { STAGE_LABELS, STAGE_COLORS } from "../../utils/stages.js";
import { parseUTC, formatDateTime } from "../../utils/date.js";
import {
  INTERVIEW_SCORE_COLORS,
  INTERVIEW_SCORE_LABELS,
  PANEL_STATUS_COLORS,
  PANEL_STATUS_LABELS,
  type InterviewScore,
  type PanelStatus,
} from "../../utils/interview.js";
import { InterviewPanel, type PanelData } from "./InterviewPanel.js";
import { CandidateProgress } from "./CandidateProgress.js";

interface HistoryItem {
  id: number;
  fromStage: string | null;
  toStage: string;
  note: string | null;
  rejectionReason: string | null;
  refUrl: string | null;
  createdAt: string;
  changedByName: string | null;
}

interface NoteItem {
  id: number;
  content: string;
  createdAt: string;
  authorId: number;
  authorName: string;
}

interface ApplicationField {
  name: string;
  label: string;
  type: string;
}

interface SubmissionField {
  name: string;
  label: string;
  type: string;
}

interface CandidateData {
  id: number;
  jobSlug: string;
  jobTitle: string;
  hiringManager: string | null;
  name: string;
  email: string;
  phone: string | null;
  linkedinUrl: string | null;
  availableStartDate: string | null;
  whyGoodFit: string | null;
  usWorkAuth: boolean | null;
  stage: string;
  flagged: boolean;
  yearsExperience: number | null;
  lastTitle: string | null;
  evalScore: number | null;
  evalRationale: string | null;
  evalCriteriaId: number | null;
  finalInterviewScore: InterviewScore | null;
  // Null when the viewer isn't allowed to see panel metadata (non-HM,
  // non-admin, non-panelist viewing a pre-interview_complete candidate).
  panelStatus: PanelStatus | null;
  panelTotal: number;
  panelSubmitted: number;
  panel: PanelData | null;
  resumePath: string | null;
  resumeFilename: string | null;
  applicationData: Record<string, string>;
  applicationFields: ApplicationField[];
  hasAssignment: boolean;
  assignmentId: number | null;
  assignmentTitle: string | null;
  assignmentPdfPath: string | null;
  assignmentSentAt: string | null;
  assignmentSubmittedAt: string | null;
  assignmentData: Record<string, string>;
  assignmentDeadline: string | null;
  assignmentSubmissionFields: SubmissionField[];
  createdAt: string;
  updatedAt: string;
  history: HistoryItem[];
  notes: NoteItem[];
  allowedTransitions: string[];
  rejectionReasons: Record<string, string>;
}

const URL_RE = /https?:\/\/[^\s<>"']+/g;

function Linkify({ text, className }: { text: string; className?: string }) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  URL_RE.lastIndex = 0;
  while ((match = URL_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const url = match[0];
    if (isSafeUrl(url)) {
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-700 hover:text-primary-800 underline"
        >
          {url}
        </a>,
      );
    } else {
      parts.push(url);
    }
    lastIndex = URL_RE.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return <span className={className}>{parts}</span>;
}

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

// interview_complete intentionally omitted: the only path into that stage
// is the panel-finalize endpoint, not the generic stage-change flow that
// drives these buttons.
const TRANSITION_LABELS: Record<string, { label: string; color: string }> = {
  assignment: { label: "Send Assignment", color: "bg-yellow-600 hover:bg-yellow-700" },
  review: { label: "Move to Review", color: "bg-purple-600 hover:bg-purple-700" },
  interview: { label: "Invite to Interview", color: "bg-indigo-600 hover:bg-indigo-700" },
  offer_pending: { label: "Extend Offer", color: "bg-green-600 hover:bg-green-700" },
  offer: { label: "Approve Hire", color: "bg-emerald-600 hover:bg-emerald-700" },
  rejected: { label: "Reject", color: "bg-red-600 hover:bg-red-700" },
  withdrawn: { label: "Mark Withdrawn", color: "bg-gray-600 hover:bg-gray-700" },
  talent_pool: { label: "Move to Talent Pool", color: "bg-amber-600 hover:bg-amber-700" },
};

export function CandidateDetail() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const [candidate, setCandidate] = useState<CandidateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [stageNote, setStageNote] = useState("");
  const [stageRef, setStageRef] = useState("");
  const [stageRejectionReason, setStageRejectionReason] = useState("");
  const [changingStage, setChangingStage] = useState<string | null>(null);
  const [overridePanelDecision, setOverridePanelDecision] = useState(false);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagNote, setFlagNote] = useState("");
  const [flagging, setFlagging] = useState(false);

  useEffect(() => {
    api
      .get<CandidateData>(`/candidates/${id}`)
      .then((d) => setCandidate(d))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load candidate"))
      .finally(() => setLoading(false));
  }, [id]);

  const refetch = () => {
    api.get<CandidateData>(`/candidates/${id}`).then(setCandidate);
  };

  if (loading) {
    return <div className="text-gray-400 animate-pulse">Loading candidate...</div>;
  }

  if (error || !candidate) {
    return <div className="text-red-600">Error: {error ?? "Candidate not found"}</div>;
  }

  const isHiringManager =
    user &&
    candidate.hiringManager &&
    user.email.toLowerCase() === candidate.hiringManager.toLowerCase();
  const canChangeStage = isHiringManager || user?.isAdmin;

  const handleStageChange = async (newStage: string) => {
    setChangingStage(newStage);
    try {
      await api.patch(`/candidates/${id}/stage`, {
        stage: newStage,
        note: stageNote || undefined,
        ref: stageRef || undefined,
        rejectionReason: newStage === "rejected" ? stageRejectionReason || undefined : undefined,
      });
      setStageNote("");
      setStageRef("");
      setStageRejectionReason("");
      refetch();
    } finally {
      setChangingStage(null);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSubmittingNote(true);
    try {
      const note = await api.post<NoteItem>(`/candidates/${id}/notes`, {
        content: noteText.trim(),
      });
      setCandidate((prev) => (prev ? { ...prev, notes: [note, ...prev.notes] } : prev));
      setNoteText("");
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleEditNote = async (noteId: number) => {
    if (!editNoteText.trim()) return;
    await api.put(`/candidates/${id}/notes/${noteId}`, { content: editNoteText.trim() });
    setCandidate((prev) =>
      prev
        ? {
            ...prev,
            notes: prev.notes.map((n) =>
              n.id === noteId ? { ...n, content: editNoteText.trim() } : n,
            ),
          }
        : prev,
    );
    setEditingNoteId(null);
    setEditNoteText("");
  };

  const handleDeleteNote = async (noteId: number) => {
    await api.delete(`/candidates/${id}/notes/${noteId}`);
    setCandidate((prev) =>
      prev ? { ...prev, notes: prev.notes.filter((n) => n.id !== noteId) } : prev,
    );
  };

  const handleFlag = async () => {
    if (!flagNote.trim()) return;
    setFlagging(true);
    try {
      await api.patch(`/candidates/${id}/flag`, { flagged: true, note: flagNote.trim() });
      setFlagModalOpen(false);
      setFlagNote("");
      refetch();
    } finally {
      setFlagging(false);
    }
  };

  const handleUnflag = async () => {
    setFlagging(true);
    try {
      await api.patch(`/candidates/${id}/flag`, { flagged: false });
      refetch();
    } finally {
      setFlagging(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/candidates"
            className="text-sm text-primary-700 hover:text-primary-800 mb-2 block"
          >
            &larr; All Candidates
          </Link>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{candidate.name}</h1>
            {candidate.flagged && (
              <span className="text-red-500 text-xl" title="Flagged">
                &#9873;
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {candidate.email} &middot; {candidate.jobTitle}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {candidate.flagged ? (
            <button
              onClick={handleUnflag}
              disabled={flagging}
              className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              {flagging ? "..." : "Unflag"}
            </button>
          ) : (
            <button
              onClick={() => setFlagModalOpen(true)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Flag
            </button>
          )}
          {candidate.panelStatus &&
            (candidate.stage === "interview" || candidate.stage === "interview_complete") && (
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${PANEL_STATUS_COLORS[candidate.panelStatus]}`}
                title={
                  candidate.panelStatus === "collecting"
                    ? `${candidate.panelSubmitted} of ${candidate.panelTotal} interviewers submitted`
                    : undefined
                }
              >
                {PANEL_STATUS_LABELS[candidate.panelStatus]}
                {candidate.panelStatus === "collecting" && candidate.panelTotal > 0 && (
                  <span className="ml-1 opacity-70">
                    {candidate.panelSubmitted} / {candidate.panelTotal}
                  </span>
                )}
              </span>
            )}
        </div>
      </div>

      <CandidateProgress
        stage={candidate.stage}
        hasAssignment={candidate.hasAssignment}
        previousStage={candidate.history[0]?.fromStage}
        completedStages={candidate.history.map((item) => item.toStage)}
      />

      {/* Flag modal */}
      {flagModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Flag candidate</h3>
            <p className="mt-2 text-sm text-gray-600">
              Provide a reason for flagging <span className="font-medium">{candidate.name}</span>.
            </p>
            <textarea
              className="mt-3 w-full rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-red-500 focus:ring-1 focus:ring-red-500"
              placeholder="Reason for flagging (required)..."
              rows={3}
              value={flagNote}
              onChange={(e) => setFlagNote(e.target.value)}
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setFlagModalOpen(false);
                  setFlagNote("");
                }}
                disabled={flagging}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleFlag}
                disabled={flagging || !flagNote.trim()}
                className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {flagging ? "Flagging..." : "Flag"}
              </button>
            </div>
          </div>
        </div>
      )}

      {candidate.evalScore != null && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 mb-6">
          <div className="flex items-start gap-4">
            <div className="text-center">
              <div
                className={`text-3xl font-bold ${
                  candidate.evalScore >= 8
                    ? "text-green-700"
                    : candidate.evalScore >= 5
                      ? "text-yellow-700"
                      : "text-red-600"
                }`}
              >
                {candidate.evalScore}/10
              </div>
              <div className="text-xs text-gray-500 mt-0.5">Eval Score</div>
            </div>
            {candidate.evalRationale && (
              <div className="flex-1 text-sm text-gray-700 border-l border-blue-200 pl-4">
                <span className="font-medium text-gray-600 text-xs uppercase tracking-wider">
                  AI Rationale
                </span>
                <p className="mt-1">{candidate.evalRationale}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {candidate.finalInterviewScore && (
        <div className="rounded-lg border border-teal-200 bg-teal-50 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-teal-700">
                Final Interview Score
              </div>
              <div className="mt-1">
                <span
                  className={`inline-block rounded-full px-3 py-1 text-sm font-semibold ${INTERVIEW_SCORE_COLORS[candidate.finalInterviewScore]}`}
                >
                  {INTERVIEW_SCORE_LABELS[candidate.finalInterviewScore]}
                </span>
              </div>
            </div>
            <div className="text-xs text-teal-800 border-l border-teal-200 pl-4">
              The interview panel has completed its evaluation. Review the full panel feedback below
              before extending an offer or rejecting.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Application Data */}
          <Section title="Application">
            <dl className="space-y-3">
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </dt>
                <dd className="mt-0.5 text-sm">
                  <a
                    href={`mailto:${candidate.email}`}
                    className="text-primary-700 hover:text-primary-800"
                  >
                    {candidate.email}
                  </a>
                </dd>
              </div>
              {candidate.phone && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </dt>
                  <dd className="mt-0.5 text-sm text-gray-900">{candidate.phone}</dd>
                </div>
              )}
              {candidate.linkedinUrl && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    LinkedIn
                  </dt>
                  <dd className="mt-0.5 text-sm">
                    {isSafeUrl(candidate.linkedinUrl) ? (
                      <a
                        href={candidate.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-700 hover:text-primary-800"
                      >
                        {candidate.linkedinUrl}
                      </a>
                    ) : (
                      <span className="text-gray-900">{candidate.linkedinUrl}</span>
                    )}
                  </dd>
                </div>
              )}
              {candidate.availableStartDate && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Available Start Date
                  </dt>
                  <dd className="mt-0.5 text-sm text-gray-900">
                    {(() => {
                      const parts = candidate.availableStartDate!.split("-").map(Number);
                      return new Date(parts[0]!, parts[1]! - 1, parts[2]).toLocaleDateString(
                        "en-US",
                        { month: "long", day: "numeric", year: "numeric" },
                      );
                    })()}
                  </dd>
                </div>
              )}
              {candidate.yearsExperience != null && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Years of Experience
                  </dt>
                  <dd className="mt-0.5 text-sm text-gray-900">{candidate.yearsExperience}</dd>
                </div>
              )}
              {candidate.lastTitle && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current / Last Title
                  </dt>
                  <dd className="mt-0.5 text-sm text-gray-900">{candidate.lastTitle}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  US Work Authorization
                </dt>
                <dd className="mt-0.5 text-sm text-gray-900">
                  {candidate.usWorkAuth ? (
                    <span className="text-green-700">Yes</span>
                  ) : (
                    <span className="text-red-600">No</span>
                  )}
                </dd>
              </div>
              {candidate.whyGoodFit && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Why they&rsquo;re a good fit
                  </dt>
                  <dd className="mt-0.5 text-sm text-gray-900 whitespace-pre-wrap">
                    {candidate.whyGoodFit}
                  </dd>
                </div>
              )}
              {candidate.applicationFields.map((field) => {
                const value = candidate.applicationData[field.name];
                if (!value) return null;
                return (
                  <div key={field.name}>
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {field.label}
                    </dt>
                    <dd className="mt-0.5 text-sm text-gray-900 whitespace-pre-wrap">{value}</dd>
                  </div>
                );
              })}
              {candidate.resumePath && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resume
                  </dt>
                  <dd className="mt-0.5">
                    <a
                      href={`/uploads/${candidate.resumePath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-700 hover:text-primary-800"
                    >
                      {candidate.resumeFilename ?? "View resume"}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </Section>

          {/* Assignment */}
          {candidate.assignmentSentAt && (
            <Section title="Assignment">
              {candidate.assignmentSubmittedAt ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="inline-block rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                      Submitted
                    </span>
                    <span className="text-gray-500">
                      {formatDateTime(candidate.assignmentSubmittedAt)}
                    </span>
                  </div>
                  {candidate.assignmentSubmissionFields.length > 0 && (
                    <dl className="space-y-3 mt-3">
                      {candidate.assignmentSubmissionFields.map((field) => {
                        const value = candidate.assignmentData[field.name];
                        if (!value) return null;
                        return (
                          <div key={field.name}>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {field.label}
                            </dt>
                            <dd className="mt-0.5 text-sm text-gray-900 whitespace-pre-wrap break-all">
                              {field.type === "url" && isSafeUrl(value) ? (
                                <a
                                  href={value}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-700 hover:text-primary-800"
                                >
                                  {value}
                                </a>
                              ) : (
                                value
                              )}
                            </dd>
                          </div>
                        );
                      })}
                    </dl>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="inline-block rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                      Pending
                    </span>
                    <span className="text-gray-500">
                      Sent {formatDateTime(candidate.assignmentSentAt)}
                    </span>
                  </div>
                  {candidate.assignmentDeadline && (
                    <p className="text-sm text-gray-500">
                      Deadline:{" "}
                      <span
                        className={
                          parseUTC(candidate.assignmentDeadline) < new Date()
                            ? "text-red-600 font-medium"
                            : "text-gray-700"
                        }
                      >
                        {formatDateTime(candidate.assignmentDeadline)}
                      </span>
                      {parseUTC(candidate.assignmentDeadline) < new Date() && (
                        <span className="ml-1.5 text-red-600 font-medium">Overdue</span>
                      )}
                    </p>
                  )}
                </div>
              )}
            </Section>
          )}

          {/* Assignment link — always visible when linked */}
          {candidate.assignmentId && candidate.assignmentPdfPath && (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm">
              <span className="text-gray-500">Assignment:</span>
              <a
                href={`/uploads/${candidate.assignmentPdfPath}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary-700 hover:text-primary-800"
              >
                {candidate.assignmentTitle ?? `#${candidate.assignmentId}`}
              </a>
            </div>
          )}

          {/* Interview Panel */}
          {user &&
            (candidate.stage === "interview" ||
              candidate.stage === "interview_complete" ||
              candidate.panel) && (
              <InterviewPanel
                candidateId={candidate.id}
                candidateName={candidate.name}
                hiringManagerEmail={candidate.hiringManager}
                currentUserEmail={user.email}
                panel={candidate.panel}
                canCreatePanel={!!canChangeStage && candidate.stage === "interview"}
                onChange={refetch}
              />
            )}

          {/* Stage Actions */}
          {canChangeStage &&
            candidate.allowedTransitions.length > 0 &&
            (() => {
              // After the interview panel finalizes a decision we narrow the
              // visible action to the one that matches the panel's verdict.
              // The opposite action is still reachable via an explicit override
              // so the hiring manager can flip it when something changed after
              // the decision (references, comp, candidate pulled out, etc.).
              const panelRecommendation: string | null =
                candidate.stage === "interview_complete" && candidate.finalInterviewScore
                  ? candidate.finalInterviewScore === "hire" ||
                    candidate.finalInterviewScore === "strong_hire"
                    ? "offer_pending"
                    : "rejected"
                  : null;

              // Talent Pool is a keep-in-touch outcome, not the opposite of
              // the panel verdict, so keep it visible without a full override
              // (override still unlocks Offer after a no-hire, and Reject after a hire).
              const visibleTransitions: string[] =
                panelRecommendation && !overridePanelDecision
                  ? [
                      panelRecommendation,
                      ...(candidate.allowedTransitions.includes("talent_pool")
                        ? (["talent_pool"] as const)
                        : []),
                    ]
                  : candidate.allowedTransitions;

              const showRejectionReasonPicker = visibleTransitions.includes("rejected");

              return (
                <Section title="Actions">
                  <div className="space-y-3">
                    {panelRecommendation && (
                      <div className="rounded-md border border-primary-100 bg-primary-50 px-3 py-2 text-xs text-primary-800">
                        <span className="font-semibold">Panel recommended:</span>{" "}
                        {INTERVIEW_SCORE_LABELS[candidate.finalInterviewScore!]} &mdash;{" "}
                        {TRANSITION_LABELS[panelRecommendation]?.label}
                        {" is the next step."}
                      </div>
                    )}
                    {showRejectionReasonPicker && (
                      <select
                        value={stageRejectionReason}
                        onChange={(e) => setStageRejectionReason(e.target.value)}
                        className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      >
                        <option value="">Select rejection reason...</option>
                        {Object.entries(candidate.rejectionReasons).map(([code, label]) => (
                          <option key={code} value={code}>
                            {label}
                          </option>
                        ))}
                      </select>
                    )}
                    <textarea
                      className="w-full rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder={
                        visibleTransitions.includes("talent_pool")
                          ? "For Talent Pool, describe the future role or strengths (required). Otherwise optional..."
                          : "Optional note for additional context..."
                      }
                      rows={2}
                      value={stageNote}
                      onChange={(e) => setStageNote(e.target.value)}
                    />
                    <input
                      type="url"
                      className="w-full rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder="Reference URL (optional, e.g. Slack thread)..."
                      value={stageRef}
                      onChange={(e) => setStageRef(e.target.value)}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      {visibleTransitions.map((stage) => {
                        const config = TRANSITION_LABELS[stage];
                        if (!config) return null;
                        const rejectDisabled = stage === "rejected" && !stageRejectionReason;
                        const talentPoolDisabled = stage === "talent_pool" && !stageNote.trim();
                        const actionDisabled = rejectDisabled || talentPoolDisabled;
                        return (
                          <button
                            key={stage}
                            onClick={() => handleStageChange(stage)}
                            disabled={changingStage !== null || actionDisabled}
                            title={
                              rejectDisabled
                                ? "Select a rejection reason first"
                                : talentPoolDisabled
                                  ? "Add a note describing why they belong in the talent pool"
                                  : undefined
                            }
                            className={`rounded px-3 py-1.5 text-sm font-medium text-white ${config.color} disabled:opacity-50 transition-colors`}
                          >
                            {changingStage === stage ? "..." : config.label}
                          </button>
                        );
                      })}
                      {panelRecommendation && !overridePanelDecision && (
                        <button
                          type="button"
                          onClick={() => setOverridePanelDecision(true)}
                          className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2"
                        >
                          Override panel decision
                        </button>
                      )}
                      {panelRecommendation && overridePanelDecision && (
                        <button
                          type="button"
                          onClick={() => {
                            setOverridePanelDecision(false);
                            setStageRejectionReason("");
                          }}
                          className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2"
                        >
                          Cancel override
                        </button>
                      )}
                    </div>
                  </div>
                </Section>
              );
            })()}
          {!canChangeStage && candidate.allowedTransitions.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
              Only the hiring manager ({candidate.hiringManager}) or an admin can change this
              candidate&rsquo;s stage.
            </div>
          )}

          {/* Notes */}
          <Section title="Notes">
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Add a note..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
                />
                <button
                  onClick={handleAddNote}
                  disabled={submittingNote || !noteText.trim()}
                  className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 transition-colors"
                >
                  Add
                </button>
              </div>
              {candidate.notes.length === 0 ? (
                <p className="text-sm text-gray-400">No notes yet.</p>
              ) : (
                <div className="space-y-2">
                  {candidate.notes.map((note) => {
                    const canModify = user && (user.id === note.authorId || user.isAdmin);
                    const isEditing = editingNoteId === note.id;
                    return (
                      <div key={note.id} className="rounded bg-gray-50 px-3 py-2 group">
                        {isEditing ? (
                          <div className="space-y-2">
                            <textarea
                              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                              rows={2}
                              value={editNoteText}
                              onChange={(e) => setEditNoteText(e.target.value)}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditNote(note.id)}
                                disabled={!editNoteText.trim()}
                                className="rounded bg-gray-900 px-3 py-1 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingNoteId(null);
                                  setEditNoteText("");
                                }}
                                className="rounded border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm text-gray-900">
                              <Linkify text={note.content} />
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-gray-400">
                                {note.authorName} &middot; {formatDateTime(note.createdAt)}
                              </p>
                              {canModify && (
                                <span className="hidden group-hover:inline-flex gap-1">
                                  <button
                                    onClick={() => {
                                      setEditingNoteId(note.id);
                                      setEditNoteText(note.content);
                                    }}
                                    className="text-xs text-gray-400 hover:text-primary-700"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteNote(note.id)}
                                    className="text-xs text-gray-400 hover:text-red-600"
                                  >
                                    Delete
                                  </button>
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Section>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Info */}
          <Section title="Details">
            <dl className="space-y-2 text-sm">
              <InfoRow label="Position" value={candidate.jobTitle} />
              <InfoRow label="Applied" value={formatDateTime(candidate.createdAt)} />
              <div>
                <dt className="text-xs text-gray-500">Time in Pipeline</dt>
                <dd className="text-gray-900">
                  {(() => {
                    const days = Math.floor(
                      (Date.now() - parseUTC(candidate.createdAt).getTime()) / 86400000,
                    );
                    if (days === 0) return "Today";
                    if (days === 1) return "1 day";
                    return `${days} days`;
                  })()}
                </dd>
              </div>
              {candidate.hiringManager && (
                <InfoRow label="Hiring Manager" value={candidate.hiringManager} />
              )}
              {candidate.assignmentSentAt && (
                <InfoRow
                  label="Assignment Sent"
                  value={formatDateTime(candidate.assignmentSentAt)}
                />
              )}
              {candidate.assignmentDeadline && !candidate.assignmentSubmittedAt && (
                <div>
                  <dt className="text-xs text-gray-500">Assignment Deadline</dt>
                  <dd>
                    {(() => {
                      const deadline = parseUTC(candidate.assignmentDeadline!);
                      const now = new Date();
                      const diffMs = deadline.getTime() - now.getTime();
                      const diffDays = Math.floor(diffMs / 86400000);
                      const formatted = formatDateTime(candidate.assignmentDeadline!);
                      if (diffDays < 0)
                        return (
                          <span className="text-red-600 font-medium">
                            {formatted} (Overdue by {Math.abs(diffDays)}d)
                          </span>
                        );
                      if (diffDays <= 2)
                        return (
                          <span className="text-yellow-600 font-medium">
                            {formatted} ({diffDays}d left)
                          </span>
                        );
                      return (
                        <span className="text-gray-900">
                          {formatted} ({diffDays}d left)
                        </span>
                      );
                    })()}
                  </dd>
                </div>
              )}
              {candidate.assignmentSubmittedAt && (
                <InfoRow
                  label="Assignment Submitted"
                  value={formatDateTime(candidate.assignmentSubmittedAt)}
                />
              )}
            </dl>
          </Section>

          {/* History */}
          <Section title="History">
            {candidate.history.length === 0 ? (
              <p className="text-sm text-gray-400">No history.</p>
            ) : (
              <div className="space-y-3">
                {candidate.history.map((h) => (
                  <div key={h.id} className="text-sm">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_COLORS[h.toStage] ?? "bg-gray-100 text-gray-600"}`}
                      >
                        {STAGE_LABELS[h.toStage] ?? h.toStage}
                      </span>
                      {h.fromStage && (
                        <span className="text-xs text-gray-400">
                          from {STAGE_LABELS[h.fromStage] ?? h.fromStage}
                        </span>
                      )}
                    </div>
                    {h.rejectionReason && candidate.rejectionReasons[h.rejectionReason] && (
                      <p className="text-xs font-medium text-red-600 mt-0.5">
                        {candidate.rejectionReasons[h.rejectionReason]}
                      </p>
                    )}
                    {h.note && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        <Linkify text={h.note} />
                      </p>
                    )}
                    {h.refUrl && (
                      <p className="text-xs mt-0.5">
                        {isSafeUrl(h.refUrl) ? (
                          <a
                            href={h.refUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-700 hover:text-primary-800 underline"
                          >
                            {h.refUrl.length > 60 ? h.refUrl.slice(0, 60) + "…" : h.refUrl}
                          </a>
                        ) : (
                          <span className="text-gray-500">
                            {h.refUrl.length > 60 ? h.refUrl.slice(0, 60) + "…" : h.refUrl}
                          </span>
                        )}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatDateTime(h.createdAt)}
                      {h.changedByName && ` by ${h.changedByName}`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-gray-900 mb-3">{title}</h2>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-gray-900">{value}</dd>
    </div>
  );
}
